import { z } from 'zod';
import { DIET_TYPE } from '../enums';
import { orderLineQuantitySchema } from '../order-lines';
export const IPD_ORDER_KINDS = [
    'medication',
    'iv_fluid',
    'diet',
    'nursing',
    'procedure',
    'lab',
    'radiology',
    'blood_transfusion',
    'oxygen',
    'physiotherapy',
    'consult',
    'vitals_monitoring',
];
export const IPD_ORDER_PRIORITY = ['routine', 'urgent', 'stat'];
export const IPD_ORDER_STATUS = ['active', 'discontinued', 'completed'];
export const IPD_ORDER_STATE = ['active', 'held', 'stopped', 'completed'];
export const IPD_ORDER_LINK_TYPES = ['clinical_order', 'diet_order'];
export const IPD_FREQUENCY_CODES = ['once', 'stat', 'od', 'bd', 'tds', 'qds', 'hs', 'q1h', 'q2h', 'q3h', 'q4h', 'q6h', 'q8h', 'q12h', 'q24h'];
export const IPD_TIMED_FREQUENCIES = ['od', 'bd', 'tds', 'qds', 'hs', 'q4h', 'q6h', 'q8h', 'q12h', 'q24h'];
export const IPD_STEPPED_FREQUENCY_MINUTES = Object.freeze({ q1h: 60, q2h: 120, q3h: 180 });
export const IPD_MED_ROUTES = [
    'oral',
    'iv',
    'im',
    'sc',
    'sl',
    'buccal',
    'inhaled',
    'nebulised',
    'topical',
    'transdermal',
    'pr',
    'pv',
    'ng',
    'peg',
    'ophthalmic',
    'otic',
    'nasal',
    'intradermal',
    'epidural',
    'intrathecal',
    'other',
];
export const IPD_DIET_TEXTURES = ['regular', 'soft', 'minced', 'pureed', 'liquid'];
export const IPD_BLOOD_PRODUCTS = ['whole_blood', 'packed_red_cells', 'fresh_frozen_plasma', 'platelets', 'cryoprecipitate'];
export const IPD_TRANSFUSION_DEFAULT_MINUTES = Object.freeze({
    whole_blood: 180,
    packed_red_cells: 120,
    fresh_frozen_plasma: 30,
    platelets: 30,
    cryoprecipitate: 15,
});
export const IPD_OXYGEN_DEVICES = ['nasal_cannula', 'simple_mask', 'venturi_mask', 'non_rebreather', 'high_flow_nasal', 'cpap', 'bipap', 'ventilator'];
export const IPD_PHYSIOTHERAPY_TYPES = ['chest', 'mobilisation', 'limb_exercises', 'gait_training', 'positioning', 'other'];
export const IPD_CONSULT_URGENCY = ['routine', 'urgent', 'emergency'];
export const IPD_VITALS_PARAMETERS = ['bp', 'pulse', 'respiratory_rate', 'temperature', 'spo2', 'pain', 'blood_sugar', 'consciousness'];
export const IPD_DEFAULT_VITALS_PARAMETERS = ['bp', 'pulse', 'respiratory_rate', 'temperature', 'spo2'];
export const IPD_IV_SITE_CONDITIONS = ['ok', 'redness', 'swelling', 'pain', 'leaking', 'infiltrated', 'phlebitis'];
export const IPD_CONSCIOUSNESS_LEVELS = ['alert', 'voice', 'pain', 'unresponsive'];
export const IPD_TASK_KINDS = [
    'dose',
    'iv_start',
    'iv_check',
    'iv_end',
    'meal',
    'nursing',
    'vitals_round',
    'transfusion_check',
    'o2_check',
    'physio',
    'consult_due',
    'procedure_due',
    'collect',
];
export const IPD_TASK_PENDING = 'due';
export const IPD_TASK_STATUS = ['due', 'given', 'given_late', 'held', 'refused', 'not_given', 'missed'];
export const IPD_TASK_DISPLAY_STATUS = ['upcoming', 'due', 'overdue', 'missed', 'given', 'given_late', 'held', 'refused', 'not_given'];
export const IPD_TASK_OUTCOMES = ['given', 'held', 'refused', 'not_given', 'missed'];
export const IPD_TASK_REASON_CODES = [
    'patient_refused',
    'family_refused',
    'patient_npo',
    'patient_away',
    'patient_asleep',
    'vomiting',
    'clinical_hold',
    'vitals_out_of_range',
    'result_awaited',
    'doctor_order',
    'drug_unavailable',
    'iv_access_lost',
    'equipment_unavailable',
    'workload',
    'late_documentation',
    'not_documented',
    'other',
];
export const IPD_TASK_REASONS_BY_OUTCOME = Object.freeze({
    held: ['clinical_hold', 'vitals_out_of_range', 'result_awaited', 'patient_npo', 'doctor_order', 'other'],
    refused: ['patient_refused', 'family_refused', 'other'],
    not_given: ['patient_away', 'patient_asleep', 'vomiting', 'drug_unavailable', 'iv_access_lost', 'equipment_unavailable', 'patient_npo', 'other'],
    missed: ['not_documented', 'patient_away', 'workload', 'drug_unavailable', 'other'],
    given_late: ['patient_away', 'workload', 'drug_unavailable', 'late_documentation', 'other'],
});
export const IPD_HOLDABLE_ORDER_KINDS = ['medication', 'iv_fluid', 'nursing', 'blood_transfusion', 'oxygen', 'physiotherapy', 'vitals_monitoring'];
export const IPD_ORDER_NOT_HOLDABLE = 'This kind of order is not held; stop it, or for a diet order nil by mouth instead';
export const IPD_HOLD_REASON_CODES = ['clinical_hold', 'vitals_out_of_range', 'result_awaited', 'patient_npo', 'procedure', 'patient_away', 'other'];
export const IPD_TRANSFUSION_STEPS = ['pre_vitals', 'start', 'check_15', 'check', 'end', 'post_vitals'];
export const IPD_READING_TASK_KINDS = ['vitals_round', 'iv_check', 'o2_check', 'transfusion_check'];
export const IPD_WITNESSED_INFUSION_TASKS = ['iv_start'];
export function ipdTaskNeedsWitness(taskKind, order) {
    if (!order.isHighAlert)
        return false;
    if (taskKind === 'dose')
        return true;
    return order.kind === 'iv_fluid' && IPD_WITNESSED_INFUSION_TASKS.includes(taskKind);
}
const instant = z.string().datetime();
const uuid = z.string().uuid();
const timeOfDaySchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a time such as 08:00');
const vitalsIntervalSchema = z
    .number()
    .int()
    .min(0)
    .max(1440)
    .refine((minutes) => minutes === 0 || minutes >= 15, 'Choose 0 for none, or at least 15 minutes');
export const IPD_OVERDUE_ESCALATION_DEFAULT_MINUTES = 60;
export const IPD_OVERDUE_ESCALATION_MAX_MINUTES = 720;
export const IPD_OVERDUE_ESCALATION_CHOICES = [0, 30, 60, 120, 240];
export const IPD_OVERDUE_ESCALATION_MESSAGE = 'Choose 0 for off, or 15 to 720 minutes';
const overdueEscalationSchema = z
    .number()
    .int()
    .min(0)
    .max(IPD_OVERDUE_ESCALATION_MAX_MINUTES, IPD_OVERDUE_ESCALATION_MESSAGE)
    .refine((minutes) => minutes === 0 || minutes >= 15, IPD_OVERDUE_ESCALATION_MESSAGE);
export const ipdScheduleSettingsSchema = z.object({
    ipdDoseTimes: z.record(z.enum(IPD_TIMED_FREQUENCIES), z.array(timeOfDaySchema).min(1).max(24)).optional(),
    ipdMealTimes: z.array(timeOfDaySchema).min(1).max(6).optional(),
    ipdVitalsIntervalMinutes: vitalsIntervalSchema.optional(),
    ipdWardVitalsIntervalMinutes: z
        .record(uuid, vitalsIntervalSchema)
        .refine((wards) => Object.keys(wards).length <= 200, 'Up to 200 wards')
        .optional(),
    ipdOverdueEscalationMinutes: overdueEscalationSchema.optional(),
});
export const IPD_SCHEDULE_SETTING_KEYS = ['ipdDoseTimes', 'ipdMealTimes', 'ipdVitalsIntervalMinutes', 'ipdWardVitalsIntervalMinutes', 'ipdOverdueEscalationMinutes'];
const chargeLinesSchema = z
    .array(z.object({ chargeItemId: uuid, quantity: orderLineQuantitySchema.default(1) }))
    .min(1)
    .max(20);
export const medicationOrderDetailsSchema = z.object({
    kind: z.literal('medication'),
    productId: uuid.nullable().optional(),
    drugName: z.string().trim().min(1).max(240),
    dose: z.number().positive().max(1_000_000),
    doseUnit: z.string().trim().min(1).max(20),
    route: z.enum(IPD_MED_ROUTES),
    frequency: z.enum(IPD_FREQUENCY_CODES).optional(),
    intervalMinutes: z.number().int().min(15).max(10_080).optional(),
    prn: z.boolean().default(false),
    prnIndication: z.string().trim().max(240).optional(),
    prnMaxDosesPerDay: z.number().int().min(1).max(24).optional(),
    durationDays: z.number().int().min(1).max(365).optional(),
    firstDoseAt: instant.optional(),
    isHighAlert: z.boolean().default(false),
});
export const ivFluidOrderDetailsSchema = z.object({
    kind: z.literal('iv_fluid'),
    productId: uuid.nullable().optional(),
    fluid: z.string().trim().min(1).max(160),
    volumeMl: z.number().int().min(1).max(5000),
    rateMlPerHour: z.number().min(1).max(2000).optional(),
    durationMinutes: z.number().int().min(5).max(2880).optional(),
    additives: z
        .array(z.object({ name: z.string().trim().min(1).max(120), amount: z.string().trim().min(1).max(40) }))
        .max(5)
        .default([]),
    bags: z.number().int().min(1).max(20).default(1),
    continuous: z.boolean().default(false),
    checkIntervalMinutes: z.number().int().min(15).max(240).default(60),
    firstBagAt: instant.optional(),
    isHighAlert: z.boolean().optional(),
});
export const dietOrderDetailsSchema = z.object({
    kind: z.literal('diet'),
    dietType: z.enum(DIET_TYPE),
    texture: z.enum(IPD_DIET_TEXTURES).default('regular'),
    fluidRestrictionMlPerDay: z.number().int().min(100).max(5000).optional(),
    npoFrom: instant.optional(),
    npoUntil: instant.optional(),
    notes: z.string().trim().max(600).optional(),
});
export const nursingOrderDetailsSchema = z.object({
    kind: z.literal('nursing'),
    task: z.string().trim().min(2).max(240),
    frequency: z.enum(IPD_FREQUENCY_CODES).optional(),
    intervalMinutes: z.number().int().min(15).max(10_080).optional(),
});
export const procedureOrderDetailsSchema = z.object({
    kind: z.literal('procedure'),
    items: chargeLinesSchema,
    scheduledAt: instant.optional(),
    notes: z.string().trim().max(600).optional(),
});
export const labOrderDetailsSchema = z.object({
    kind: z.literal('lab'),
    items: chargeLinesSchema,
    collectAt: instant.optional(),
    fasting: z.boolean().default(false),
    notes: z.string().trim().max(600).optional(),
});
export const radiologyOrderDetailsSchema = z.object({
    kind: z.literal('radiology'),
    items: chargeLinesSchema,
    notes: z.string().trim().max(600).optional(),
    pregnancyChecked: z.boolean().optional(),
    pregnant: z.boolean().optional(),
    pregnancyJustification: z.string().trim().max(600).optional(),
});
export const bloodTransfusionOrderDetailsSchema = z.object({
    kind: z.literal('blood_transfusion'),
    product: z.enum(IPD_BLOOD_PRODUCTS),
    units: z.number().int().min(1).max(10),
    crossmatchRef: z.string().trim().max(60).optional(),
    volumeMlPerUnit: z.number().int().min(10).max(1000).optional(),
    rateMlPerHour: z.number().min(1).max(1000).optional(),
    durationMinutesPerUnit: z.number().int().min(10).max(480).optional(),
    indication: z.string().trim().max(400).optional(),
    firstUnitAt: instant.optional(),
});
export const oxygenOrderDetailsSchema = z.object({
    kind: z.literal('oxygen'),
    device: z.enum(IPD_OXYGEN_DEVICES),
    flowLpm: z.number().min(0.5).max(70).optional(),
    fio2: z.number().int().min(21).max(100).optional(),
    targetSpo2Min: z.number().int().min(80).max(100),
    targetSpo2Max: z.number().int().min(80).max(100),
    checkIntervalMinutes: z.number().int().min(15).max(720).default(240),
});
export const physiotherapyOrderDetailsSchema = z.object({
    kind: z.literal('physiotherapy'),
    type: z.enum(IPD_PHYSIOTHERAPY_TYPES),
    sessionsPerDay: z.number().int().min(1).max(6).default(1),
    durationDays: z.number().int().min(1).max(90).optional(),
    notes: z.string().trim().max(600).optional(),
});
export const consultOrderDetailsSchema = z.object({
    kind: z.literal('consult'),
    targetDoctorId: uuid.optional(),
    targetDepartmentId: uuid.optional(),
    specialty: z.string().trim().max(120).optional(),
    urgency: z.enum(IPD_CONSULT_URGENCY).default('routine'),
    question: z.string().trim().min(3).max(1000),
});
export const vitalsMonitoringOrderDetailsSchema = z.object({
    kind: z.literal('vitals_monitoring'),
    intervalMinutes: z.number().int().min(15).max(1440),
    parameters: z.array(z.enum(IPD_VITALS_PARAMETERS)).min(1).max(IPD_VITALS_PARAMETERS.length).default([...IPD_DEFAULT_VITALS_PARAMETERS]),
});
export const ipdOrderDetailsSchema = z.discriminatedUnion('kind', [
    medicationOrderDetailsSchema,
    ivFluidOrderDetailsSchema,
    dietOrderDetailsSchema,
    nursingOrderDetailsSchema,
    procedureOrderDetailsSchema,
    labOrderDetailsSchema,
    radiologyOrderDetailsSchema,
    bloodTransfusionOrderDetailsSchema,
    oxygenOrderDetailsSchema,
    physiotherapyOrderDetailsSchema,
    consultOrderDetailsSchema,
    vitalsMonitoringOrderDetailsSchema,
]);
export const IPD_ORDER_MESSAGES = Object.freeze({
    endsBeforeStart: 'The order cannot end before it starts',
    frequencyOrInterval: 'Give a frequency or an interval, not both',
    frequencyRequired: 'Choose how often the dose is given',
    prnIndication: 'Say when the as-needed dose may be given',
    prnNotScheduled: 'An as-needed dose has no frequency; give the most it may be given in a day instead',
    rateOrDuration: 'Give the rate or the duration of the infusion, not both',
    rateOrDurationRequired: 'Give the rate or the duration of the infusion',
    flowOrFio2: 'Give the oxygen flow or the FiO2',
    spo2Range: 'The lowest target saturation must not be above the highest',
    consultTarget: 'Choose the doctor or the department to consult',
    npoWindow: 'Nil by mouth must end after it starts',
    pregnancyJustification: 'Say why the imaging goes ahead in pregnancy',
    duplicateService: 'The same service is listed twice; raise its quantity instead',
});
export function ipdOrderProblems(order) {
    const problems = [];
    const m = IPD_ORDER_MESSAGES;
    if (order.startsAt && order.endsAt && Date.parse(order.endsAt) <= Date.parse(order.startsAt))
        problems.push({ path: 'endsAt', message: m.endsBeforeStart });
    const details = order.details;
    switch (details.kind) {
        case 'medication':
            if (details.frequency && details.intervalMinutes)
                problems.push({ path: 'details.intervalMinutes', message: m.frequencyOrInterval });
            if (details.prn) {
                if (!details.prnIndication || details.prnIndication.trim().length < 3)
                    problems.push({ path: 'details.prnIndication', message: m.prnIndication });
                if (details.frequency || details.intervalMinutes)
                    problems.push({ path: 'details.frequency', message: m.prnNotScheduled });
            }
            else if (!details.frequency && !details.intervalMinutes) {
                problems.push({ path: 'details.frequency', message: m.frequencyRequired });
            }
            break;
        case 'nursing':
            if (details.frequency && details.intervalMinutes)
                problems.push({ path: 'details.intervalMinutes', message: m.frequencyOrInterval });
            break;
        case 'iv_fluid':
            if (details.rateMlPerHour && details.durationMinutes)
                problems.push({ path: 'details.durationMinutes', message: m.rateOrDuration });
            if (!details.rateMlPerHour && !details.durationMinutes)
                problems.push({ path: 'details.rateMlPerHour', message: m.rateOrDurationRequired });
            break;
        case 'blood_transfusion':
            if (details.rateMlPerHour && details.durationMinutesPerUnit)
                problems.push({ path: 'details.durationMinutesPerUnit', message: m.rateOrDuration });
            break;
        case 'oxygen':
            if (details.flowLpm === undefined && details.fio2 === undefined)
                problems.push({ path: 'details.flowLpm', message: m.flowOrFio2 });
            if (details.targetSpo2Min > details.targetSpo2Max)
                problems.push({ path: 'details.targetSpo2Min', message: m.spo2Range });
            break;
        case 'consult':
            if (!details.targetDoctorId && !details.targetDepartmentId)
                problems.push({ path: 'details.targetDoctorId', message: m.consultTarget });
            break;
        case 'diet':
            if (details.npoFrom && details.npoUntil && Date.parse(details.npoUntil) <= Date.parse(details.npoFrom))
                problems.push({ path: 'details.npoUntil', message: m.npoWindow });
            break;
        case 'radiology':
            if (details.pregnant && (details.pregnancyJustification ?? '').length < 5)
                problems.push({ path: 'details.pregnancyJustification', message: m.pregnancyJustification });
            break;
        default:
            break;
    }
    if ('items' in details && Array.isArray(details.items)) {
        const ids = details.items.map((item) => item.chargeItemId);
        if (new Set(ids).size !== ids.length)
            problems.push({ path: 'details.items', message: m.duplicateService });
    }
    return problems;
}
export const createIpdOrderSchema = z
    .object({
    priority: z.enum(IPD_ORDER_PRIORITY).default('routine'),
    startsAt: instant.optional(),
    endsAt: instant.nullable().optional(),
    instructions: z.string().trim().max(1000).optional(),
    details: ipdOrderDetailsSchema,
})
    .superRefine((value, context) => {
    for (const problem of ipdOrderProblems(value)) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: problem.path.split('.'), message: problem.message });
    }
});
export const stopIpdOrderSchema = z.object({
    reason: z.string().trim().min(3).max(300),
});
export const holdIpdOrderSchema = z.object({
    reasonCode: z.enum(IPD_HOLD_REASON_CODES).default('clinical_hold'),
    reason: z.string().trim().min(3).max(300),
});
export const resumeIpdOrderSchema = z.object({
    reason: z.string().trim().max(300).optional(),
});
export const IPD_ORDER_LIST_STATUS = ['active', 'running', 'held', 'stopped', 'all'];
export const ipdOrderListQuerySchema = z.object({
    status: z.enum(IPD_ORDER_LIST_STATUS).default('active'),
    kind: z.enum(IPD_ORDER_KINDS).optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
    cursor: z.string().uuid().optional(),
    includeStopped: z.enum(['true', 'false']).optional(),
});
export const IPD_TASKS_CHANGED_EVENT = 'ipd.tasks.changed';
export const IPD_ORDERS_CHANGED_EVENT = 'ipd.orders.changed';
export const IPD_TASK_WINDOW_MAX_HOURS = 72;
export const IPD_TASK_WINDOW_DEFAULT_BEFORE_HOURS = 12;
export const IPD_TASK_WINDOW_DEFAULT_AFTER_HOURS = 24;
export const IPD_TASK_WINDOW_TOO_WIDE = 'Choose a window of 72 hours or less';
export const IPD_TASK_WINDOW_BACKWARDS = 'The window must end after it starts';
export const ipdTaskWindowQuerySchema = z
    .object({
    from: instant.optional(),
    to: instant.optional(),
})
    .superRefine((value, context) => {
    if (!value.from || !value.to)
        return;
    const span = Date.parse(value.to) - Date.parse(value.from);
    if (span <= 0)
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: IPD_TASK_WINDOW_BACKWARDS });
    else if (span > IPD_TASK_WINDOW_MAX_HOURS * 3_600_000)
        context.addIssue({ code: z.ZodIssueCode.custom, path: ['to'], message: IPD_TASK_WINDOW_TOO_WIDE });
});
export const ipdTaskReadingsSchema = z.object({
    bpSystolic: z.number().int().min(50).max(260).optional(),
    bpDiastolic: z.number().int().min(25).max(160).optional(),
    pulse: z.number().int().min(25).max(250).optional(),
    respiratoryRate: z.number().int().min(4).max(80).optional(),
    temperatureC: z.number().min(30).max(44.5).optional(),
    spo2: z.number().int().min(40).max(100).optional(),
    painScore: z.number().int().min(0).max(10).optional(),
    bloodSugarMgDl: z.number().int().min(10).max(1200).optional(),
    consciousness: z.enum(IPD_CONSCIOUSNESS_LEVELS).optional(),
    flowLpm: z.number().min(0).max(70).optional(),
    fio2: z.number().int().min(21).max(100).optional(),
    device: z.enum(IPD_OXYGEN_DEVICES).optional(),
    rateMlPerHour: z.number().min(0).max(2000).optional(),
    volumeInfusedMl: z.number().int().min(0).max(10_000).optional(),
    siteCondition: z.enum(IPD_IV_SITE_CONDITIONS).optional(),
    reaction: z.boolean().optional(),
    reactionNotes: z.string().trim().max(600).optional(),
});
export const IPD_READING_MESSAGES = Object.freeze({
    vitalsRequired: 'Record at least one observation for this round',
    bpPair: 'Record the systolic and the diastolic pressure together',
    spo2Required: 'Record the oxygen saturation',
    siteRequired: 'Record how the drip site looks',
    transfusionObservations: 'Record the temperature and the pulse for this transfusion check',
    reactionNotes: 'Describe the reaction',
});
const VITAL_READINGS = ['bpSystolic', 'bpDiastolic', 'pulse', 'respiratoryRate', 'temperatureC', 'spo2', 'painScore', 'bloodSugarMgDl', 'consciousness'];
export function ipdReadingProblems(taskKind, readings) {
    const r = readings ?? {};
    const problems = [];
    const m = IPD_READING_MESSAGES;
    if ((r.bpSystolic === undefined) !== (r.bpDiastolic === undefined))
        problems.push({ path: 'readings.bpSystolic', message: m.bpPair });
    if (taskKind === 'vitals_round' && !VITAL_READINGS.some((key) => r[key] !== undefined))
        problems.push({ path: 'readings.pulse', message: m.vitalsRequired });
    if (taskKind === 'o2_check' && r.spo2 === undefined)
        problems.push({ path: 'readings.spo2', message: m.spo2Required });
    if (taskKind === 'iv_check' && r.siteCondition === undefined)
        problems.push({ path: 'readings.siteCondition', message: m.siteRequired });
    if (taskKind === 'transfusion_check' && (r.temperatureC === undefined || r.pulse === undefined))
        problems.push({ path: 'readings.temperatureC', message: m.transfusionObservations });
    if (r.reaction && !r.reactionNotes)
        problems.push({ path: 'readings.reactionNotes', message: m.reactionNotes });
    return problems;
}
export const recordIpdTaskSchema = z.object({
    outcome: z.enum(IPD_TASK_OUTCOMES),
    at: instant.optional(),
    reasonCode: z.enum(IPD_TASK_REASON_CODES).optional(),
    reason: z.string().trim().max(400).optional(),
    witnessUserId: uuid.nullable().optional(),
    doseGiven: z.string().trim().max(80).optional(),
    site: z.string().trim().max(80).optional(),
    notes: z.string().max(1000).optional(),
    readings: ipdTaskReadingsSchema.optional(),
});
export const recordAdHocDoseSchema = z.object({
    at: instant.optional(),
    reason: z.string().trim().min(3).max(400),
    witnessUserId: uuid.nullable().optional(),
    doseGiven: z.string().trim().max(80).optional(),
    site: z.string().trim().max(80).optional(),
    notes: z.string().max(1000).optional(),
});
export const IPD_INVESTIGATION_ORDER_KINDS = ['lab', 'radiology', 'procedure'];
//# sourceMappingURL=ipd-orders.js.map